import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { waLink } from '@/lib/supabase';
import { getFeaturedProducts } from '@/lib/data';
import type { Product } from '@/lib/database.types';
import { HERO_IMAGES } from '@/lib/constants';
import { useReveal } from '@/hooks/useReveal';

/**
 * Tres cards destacadas siguiendo el HTML de referencia.
 * Si la BD no devuelve productos, usamos los 3 hardcodeados con sus imágenes.
 */
const HARDCODED: Array<{
  id: string;
  name: string;
  description: string;
  img: string;
  hash: string;        // ancla del catálogo
  waText: string;
}> = [
  {
    id: 'espaldilla',
    name: 'ESPALDILLA',
    description:
      'Espaldilla cocida de cerdo, elaborada con ingredientes seleccionados e importados. Textura suave y sabor único, ideal para lonches y preparaciones gourmet.',
    img: HERO_IMAGES.espaldilla,
    hash: '#piernas',
    waText: 'Hola, me interesa cotizar Espaldilla de Cerdo.',
  },
  {
    id: 'el-dorado',
    name: 'EL DORADO',
    description:
      'Jamón de pierna premium con proceso artesanal supervisado y maduración controlada. Sabor profundo y excepcional en cada rebanada.',
    img: HERO_IMAGES.pierna,
    hash: '#piernas',
    waText: 'Hola, me interesa cotizar El Dorado (Jamón de Pierna).',
  },
  {
    id: 'chorizo-especial',
    name: 'CHORIZO ESPECIAL',
    description:
      'Chorizo artesanal con especias selectas. Disponible en presentaciones frescas y ahumadas. El favorito de restaurantes y distribuidores.',
    img: HERO_IMAGES.chorizo,
    hash: '#chorizos',
    waText: 'Hola, me interesa cotizar Chorizo Especial.',
  },
];

export function FeaturedProducts() {
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    void (async () => { setProducts(await getFeaturedProducts()); })();
  }, []);

  useReveal(products.length);

  // Si la BD devolvió 3+ productos, los usamos; si no, fallback hardcoded.
  const cards = products.length >= 3
    ? products.slice(0, 3).map((p, i) => ({
        id: p.id,
        name: p.name.toUpperCase(),
        description: p.description || HARDCODED[i].description,
        img: HARDCODED[i].img,
        hash: HARDCODED[i].hash,
        waText: `Hola, me interesa cotizar ${p.name}.`,
      }))
    : HARDCODED;

  return (
    <section id="productos">
      <div className="wrap">
        <span className="eyebrow eyebrow-dk reveal">Catálogo</span>
        <h2 className="st reveal" style={{ color: 'var(--black)' }}>NUESTROS PRODUCTOS</h2>
        <p className="prod-intro reveal">
          Embutidos elaborados con materias primas seleccionadas e importadas.
          Disponibles en todas nuestras sedes.
        </p>
        <div className="cards">
          {cards.map((c, i) => (
            <article key={c.id} className={`card reveal d${i + 1}`}>
              <div className="card-img-wrap sk loaded">
                <Link to={`/catalogo${c.hash}`} className="card-img-link">
                  <img className="card-img" src={c.img} alt={c.name} loading="lazy" />
                  <div className="card-img-overlay">
                    <span className="card-img-hint">VER EN CATÁLOGO</span>
                  </div>
                </Link>
              </div>
              <div className="card-body">
                <div className="card-brand">EMPACADORA LAS POLVILLAS</div>
                <h3>{c.name}</h3>
                <p>{c.description}</p>
                <a
                  href={waLink(c.waText)}
                  target="_blank"
                  rel="noopener"
                  className="card-wa"
                >
                  <i className="fab fa-whatsapp" /> SOLICITAR COTIZACIÓN
                </a>
              </div>
            </article>
          ))}
        </div>
        <div className="catalogo-cta reveal">
          <p>Contamos con 12 productos en 5 categorías: piernas, jamones, chorizos, tocinos y especialidades.</p>
          <Link to="/catalogo" className="btn" style={{ borderColor: 'rgba(0,0,0,.3)', color: 'rgba(0,0,0,.7)' }}>
            VER CATÁLOGO COMPLETO →
          </Link>
        </div>
      </div>
    </section>
  );
}
